import React, { useState } from "react";
import {
  X,
  Briefcase,
  Mail,
  Lock,
  ArrowRight,
  CheckCircle2,
  User,
} from "lucide-react";
import { Box, CircularProgress } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { GoogleOAuthProvider, useGoogleLogin } from "@react-oauth/google";
import { postSignup } from "../../network/APIs/login";

const GoogleAuthButton = () => {
  const login = useGoogleLogin({
    onSuccess: (codeResponse) => {
      // console.log("Google login success:", codeResponse);
      alert("Google authentication successful!");
    },
    onError: () => {
      console.error("Google login failed");
      alert("Google authentication failed!");
    },
  });

  return (
    <button
      type="button"
      onClick={() => login()}
      className="w-full bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-bold py-3.5 rounded-xl transition-all flex items-center justify-center gap-3"
    >
      <svg className="w-5 h-5" viewBox="0 0 24 24">
        <path
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          fill="#4285F4"
        />
        <path
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          fill="#34A853"
        />
        <path
          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          fill="#FBBC05"
        />
        <path
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          fill="#EA4335"
        />
      </svg>
      Google
    </button>
  );
};

interface SignupPageProps {
  onBack: () => void;
  onLogin: () => void;
  onSignupSuccess?: () => void;
}

const validationSchema = Yup.object({
  name: Yup.string()
    .required("Please enter your full name")
    .min(2, "Name must be at least 2 characters")
    .max(50, "Name must not exceed 50 characters")
    .matches(/^[a-zA-Z\s]+$/, "Name can only contain letters and spaces")
    .test("no-script", "Name cannot contain scripts or HTML tags", (value) => {
      return !/<[^>]*>|javascript:|on\w+\s*=/i.test(value || "");
    }),
  email: Yup.string()
    .email("Invalid email format")
    .required("Please enter your email address")
    .test("no-script", "Email cannot contain scripts or HTML tags", (value) => {
      return !/<[^>]*>|javascript:|on\w+\s*=/i.test(value || "");
    }),
  password: Yup.string()
    .required("Please enter a strong password")
    .min(8, "Password must be at least 8 characters")
    .matches(/[a-z]/, "Password must contain at least one lowercase letter")
    .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
    .matches(/[0-9]/, "Password must contain at least one number")
    .matches(/[^a-zA-Z0-9]/, "Password must contain at least one special character")
    .test("no-script", "Password cannot contain scripts or HTML tags", (value) => {
      return !/<[^>]*>|javascript:|on\w+\s*=/i.test(value || "");
    }),
});

const SignupPage: React.FC<SignupPageProps> = ({
  onBack,
  onLogin,
  onSignupSuccess,
}) => {
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      password: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      setErrorMessage("");

      try {
        const formData = new FormData();
        formData.append("name", values.name.trim());
        formData.append("email", values.email.trim());
        formData.append("password", values.password);

        const res = await postSignup(formData);

        setLoading(false);
        if (res.success === false) {
          setErrorMessage(res.message || "Signup failed");
        } else {
          const expiryTime = new Date().getTime() + 30 * 24 * 60 * 60 * 1000; // 30 days from now
          localStorage.setItem(
            "Freshment_user_data",
            JSON.stringify({ data: res.data, expiry: expiryTime }),
          );
          formik.resetForm();
          setErrorMessage("");
          onSignupSuccess?.();
          onBack();
        }
      } catch (error: any) {
        setLoading(false);
        setErrorMessage("An error occurred. Please try again.");
      }
    },
  });

  return (
    <div className="fixed inset-0 z-[10010] bg-slate-50/95 backdrop-blur-xl flex items-center justify-center p-4 overflow-y-auto w-full h-full">
      <div className="bg-white w-full max-w-md rounded-3xl shadow-[0_20px_50px_-12px_rgba(0,0,0,0.1)] overflow-hidden relative border border-slate-100 my-auto shrink-0">
        {/* Close Button */}
        <button
          onClick={onBack}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition z-10"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="p-8 md:p-10 relative">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-brand-50 text-brand-600 mb-6 shadow-sm border border-brand-100">
              <Briefcase className="w-7 h-7" />
            </div>
            <h1 className="text-3xl font-black text-slate-900 tracking-tight mb-2 font-sans">
              Create Account
            </h1>
            <p className="text-slate-500 text-sm font-medium font-sans">
              Sign up to get started with your career
            </p>
          </div>

          <Box component="form" onSubmit={formik.handleSubmit}>
            <div className="space-y-5">
              {/* Full Name Field */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wider ml-1">
                  Full Name
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <User
                      className={`h-5 w-5 transition-colors ${formik.touched.name && formik.errors.name ? "text-red-400" : "text-slate-400 group-focus-within:text-brand-600"}`}
                    />
                  </div>
                  <input
                    type="text"
                    name="name"
                    className={`block w-full pl-11 pr-4 py-3.5 bg-slate-50 border rounded-xl text-slate-900 text-sm font-semibold placeholder-slate-400 focus:outline-none focus:ring-2 transition-all ${
                      formik.touched.name && formik.errors.name
                        ? "border-red-400 focus:ring-red-500/20 focus:border-red-500"
                        : "border-slate-200 focus:ring-brand-500/20 focus:border-brand-500"
                    }`}
                    placeholder="John Doe"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </div>
                {formik.touched.name && formik.errors.name && (
                  <p className="text-red-500 text-xs font-medium ml-1 mt-1">
                    {String(formik.errors.name)}
                  </p>
                )}
              </div>

              {/* Email Field */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wider ml-1">
                  Email Address
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Mail
                      className={`h-5 w-5 transition-colors ${formik.touched.email && formik.errors.email ? "text-red-400" : "text-slate-400 group-focus-within:text-brand-600"}`}
                    />
                  </div>
                  <input
                    type="email"
                    name="email"
                    className={`block w-full pl-11 pr-4 py-3.5 bg-slate-50 border rounded-xl text-slate-900 text-sm font-semibold placeholder-slate-400 focus:outline-none focus:ring-2 transition-all ${
                      formik.touched.email && formik.errors.email
                        ? "border-red-400 focus:ring-red-500/20 focus:border-red-500"
                        : "border-slate-200 focus:ring-brand-500/20 focus:border-brand-500"
                    }`}
                    placeholder="name@example.com"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </div>
                {formik.touched.email && formik.errors.email && (
                  <p className="text-red-500 text-xs font-medium ml-1 mt-1">
                    {String(formik.errors.email)}
                  </p>
                )}
              </div>

              {/* Password Field */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wider ml-1">
                  Password
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Lock
                      className={`h-5 w-5 transition-colors ${formik.touched.password && formik.errors.password ? "text-red-400" : "text-slate-400 group-focus-within:text-brand-600"}`}
                    />
                  </div>
                  <input
                    type="password"
                    name="password"
                    className={`block w-full pl-11 pr-4 py-3.5 bg-slate-50 border rounded-xl text-slate-900 text-sm font-semibold placeholder-slate-400 focus:outline-none focus:ring-2 transition-all ${
                      formik.touched.password && formik.errors.password
                        ? "border-red-400 focus:ring-red-500/20 focus:border-red-500"
                        : "border-slate-200 focus:ring-brand-500/20 focus:border-brand-500"
                    }`}
                    placeholder="••••••••"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </div>
                {formik.touched.password && formik.errors.password && (
                  <p className="text-red-500 text-xs font-medium ml-1 mt-1">
                    {String(formik.errors.password)}
                  </p>
                )}
              </div>
            </div>

            {errorMessage !== "" ? (
              <p className="text-red-600 text-sm mt-2">{errorMessage}</p>
            ) : null}

            {/* Signup Button */}
            <button
              type="submit"
              className="w-full mt-5 bg-brand-600 hover:bg-brand-700 text-white font-bold py-4 rounded-xl shadow-lg shadow-brand-500/30 transition-all transform active:scale-[0.98] flex items-center justify-center gap-2"
            >
              Create Account <ArrowRight className="w-5 h-5" />
            </button>

            {loading && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  backgroundColor: "rgba(255, 255, 255, 0.7)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  zIndex: 10,
                  borderRadius: "24px", // To match the modal's border radius roughly
                }}
              >
                <CircularProgress size={50} sx={{ color: "#0284c7" }} />
              </Box>
            )}
          </Box>

          {/* Divider */}
          {/* <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-3 text-slate-400 font-bold tracking-widest">
                Or continue with
              </span>
            </div>
          </div> */}

          {/* Google Signup */}
          {/* process.env.REACT_APP_GOOGLE_CLIENT_ID */}
          {/* <GoogleOAuthProvider clientId="420331851809-erj4b5md0n3qb7kgi8uo31k827d1ou0c.apps.googleusercontent.com">
            <GoogleAuthButton />
          </GoogleOAuthProvider> */}

          {/* Footer Links */}
          <div className="mt-8 text-center">
            <p className="text-slate-500 text-sm font-medium">
              Already have an account?{" "}
              <button
                onClick={onLogin}
                type="button"
                className="text-brand-600 font-bold hover:underline"
              >
                Login
              </button>
            </p>
          </div>

          {/* Trust Badge */}
          <div className="mt-8 flex items-center justify-center gap-2 text-[11px] font-bold text-slate-400 uppercase tracking-widest bg-slate-50 py-2 rounded-lg">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
            Secure & Encrypted
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
